/**
 * What the renderer needs from a match, whatever runs it: the local practice runner, or the
 * predicted-and-reconciled online session (Phase 4). The renderer only reads; the driver owns
 * the simulation, its interpolation and its clock. Nothing here decides gameplay (CLAUDE.md #1).
 */
import type { ArenaColliders, ArenaRuntime } from '@steelwake/shared/arena';
import type {
  BotLevel,
  Combatant,
  Deployable,
  MatchEvent,
  MatchState,
  Slot,
} from '@steelwake/shared/combat';
import type { Loadout } from '@steelwake/shared/loadout';
import type { MatchWorld, MechPose } from '@steelwake/shared/sim';

import type { OnlineHud } from '../../hud/types.js';
import type { NetConditions } from '../../net/conditioner.js';
import type { NetMetrics } from '../../net/metrics.js';
import type { InputSample } from '../sim/input.js';
import type { UrlOptions } from '../urlParams.js';
import type { MatchParams } from './setup.js';

export interface ViewPose {
  x: number;
  y: number;
  z: number;
  yaw: number;
  aimYaw: number;
  aimPitch: number;
}

export interface ProjectileView {
  x: number;
  y: number;
  z: number;
  weapon: string;
}

/** A mech as it should be drawn this frame: its interpolated pose and the state record behind it. */
export interface MechFrame {
  pose: ViewPose;
  combatant: Combatant;
}

/** Practice-only controls; the shared runner refuses them outside practice mode. */
export interface PracticeHooks {
  readonly params: MatchParams;
  setBotLevel(level: BotLevel): void;
  skipCountdown(): void;
  setRoundSeconds(seconds: number): void;
  ultimateReady(): void;
  /** Same setup, new seed, no asset reload. */
  rematch(seed: number): void;
  /** Puts the own mech's feet at a point with no momentum (tests only). */
  teleport(position: { x: number; y: number; z: number }): void;
}

export interface MatchDriver {
  readonly mySlot: Slot;
  readonly enemySlot: Slot;
  /** Advances the match for one rendered frame and returns the events to present now. */
  advance(frameMs: number, sample: InputSample): MatchEvent[];
  /** The state the HUD, results and readouts show (the server's truth online). */
  readonly state: MatchState;
  /** The state the player's own mech is drawn from (the prediction online; `state` in practice). */
  readonly ownState: MatchState;
  mech(slot: Slot): MechFrame;
  projectiles(): ProjectileView[];
  deployables(): readonly Deployable[];
  /** The arena features' runtime (pickup respawns, pad cooldowns): the truth online. */
  features(): ArenaRuntime;
  /** The driver's own fractional timeline, for posing platforms between ticks. */
  viewTick(): number;
  readonly finished: boolean;
  /** Present for practice matches only. */
  readonly practice: PracticeHooks | null;
  /** Connection metrics; online drivers only. */
  net?(): NetMetrics;
  /** What the HUD shows about the connection; online drivers only. */
  online?(): OnlineHud;
  /** Re-tunes the simulated link (development and e2e); online drivers only. */
  setNetConditions?(conditions: NetConditions): void;
  dispose(): void;
}

/** What a match source knows before any asset loads. */
export interface MatchDescriptor {
  mechs: [string, string];
  mySlot: Slot;
  arenaId: string;
  /** The server's fingerprint of the arena content (online); null in practice. */
  arenaHash: number | null;
  /** Each seat's loadout (Phase 6): null means the mech's stock look and no modules. */
  loadouts: [Loadout | null, Loadout | null];
  /** The published balance version the rules run under (Phase 7); 0 = the built-in data. */
  balanceVersion: number;
}

export interface DriverContext {
  world: MatchWorld;
  arena: ArenaColliders;
  e2e: boolean;
}

export interface PrepareContext {
  onStatus(message: string): void;
  url: UrlOptions;
}

/** Where a match comes from: resolved before assets load, driven once the world exists. */
export interface MatchSource {
  prepare(signal: AbortSignal, ctx: PrepareContext): Promise<MatchDescriptor>;
  createDriver(ctx: DriverContext): MatchDriver;
}

/** A drawn pose as the shared hit geometry expects it. */
export function asMechPose(v: ViewPose): MechPose {
  return {
    position: { x: v.x, y: v.y, z: v.z },
    yaw: v.yaw,
    aimYaw: v.aimYaw,
    aimPitch: v.aimPitch,
  };
}
